import { useState, useEffect } from 'react'
import type { ComplianceViolation, TenantPolicyWaiver, WaiverType, CreateWaiverRequest, UpdateWaiverRequest } from '@/types'
import { DateInput } from '@/components/carbon/DateInput'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Modal } from '@/components/carbon/Modal'
import { RadioGroup } from '@/components/carbon/RadioGroup'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { severityFromApi } from '@/components/carbon/severity'
import { TextArea } from '@/components/carbon/TextArea'

interface WaiverModalProps {
  isOpen: boolean
  onClose: () => void
  violation: ComplianceViolation | null
  onSubmit: (request: CreateWaiverRequest) => void
  isSubmitting?: boolean
  existingWaiver?: TenantPolicyWaiver
  onUpdate?: (waiverId: string, request: UpdateWaiverRequest) => void
}

interface WaiverTypeOption {
  value: WaiverType
  label: string
  description: string
}

const waiverTypes: WaiverTypeOption[] = [
  {
    value: 'Permanent',
    label: 'Alternative mitigation',
    description: 'This risk is addressed through an alternative control or compensating measure.',
  },
  {
    value: 'Temporary',
    label: 'Temporary exception',
    description: 'We acknowledge the risk and will remediate by a specific date.',
  },
  {
    value: 'NotApplicable',
    label: 'Not applicable',
    description: 'This control is not relevant to our organization or use case.',
  },
]

/**
 * Modal for adding a waiver (exception) to a compliance finding, on the
 * Carbon Modal: the finding it covers, the waiver type as selectable tiles,
 * an expiration date for temporary waivers, and the reason. Validation is
 * blocking, so its messages take the Coral field error.
 */
export default function WaiverModal({
  isOpen,
  onClose,
  violation,
  onSubmit,
  isSubmitting = false,
  existingWaiver,
  onUpdate,
}: WaiverModalProps) {
  const isEditMode = !!existingWaiver
  const [waiverType, setWaiverType] = useState<WaiverType>('Permanent')
  const [reason, setReason] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Reset form when modal opens (isOpen changes to true) or violation changes
  // In edit mode, pre-fill from existing waiver data
  useEffect(() => {
    if (isOpen) {
      if (existingWaiver) {
        setWaiverType(existingWaiver.waiverType)
        setReason(existingWaiver.reason)
        setExpiresAt(existingWaiver.expiresAt ? existingWaiver.expiresAt.split('T')[0] : '')
      } else {
        setWaiverType('Permanent')
        setReason('')
        setExpiresAt('')
      }
      setErrors({})
    }
  }, [isOpen, violation?.id, existingWaiver])

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!reason.trim()) {
      newErrors.reason = 'Add a reason for this waiver.'
    } else if (reason.trim().length < 20) {
      newErrors.reason = 'Add a little more detail: at least 20 characters.'
    }

    if (waiverType === 'Temporary') {
      if (!expiresAt) {
        newErrors.expiresAt = 'Pick an expiration date for a temporary waiver.'
      } else {
        const expDate = new Date(expiresAt)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        if (expDate <= today) {
          newErrors.expiresAt = 'Pick a date after today.'
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = () => {
    if (!violation || !validate()) return

    if (isEditMode && existingWaiver && onUpdate) {
      onUpdate(existingWaiver.id, {
        waiverType,
        reason: reason.trim(),
        ...(waiverType === 'Temporary' && { expiresAt }),
      })
    } else {
      const request: CreateWaiverRequest = {
        policyId: violation.policyId,
        waiverType,
        reason: reason.trim(),
        ...(waiverType === 'Temporary' && { expiresAt }),
      }

      onSubmit(request)
    }
  }

  // Get minimum date for expiration (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split('T')[0]
  }

  // Live preview for the "Expires in N days" hint under the date picker.
  // Computed from the date string the <input type="date"> emits (YYYY-MM-DD,
  // parsed in the user's local zone via the explicit numeric constructor so
  // the count matches what the picker shows).
  const expiryPreview: string | null = (() => {
    if (waiverType !== 'Temporary' || !expiresAt) return null
    const parts = expiresAt.split('-').map(Number)
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null
    const picked = new Date(parts[0], parts[1] - 1, parts[2])
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const days = Math.ceil((picked.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`
    if (days === 0) return 'Expires today'
    return `Expires in ${days} day${days === 1 ? '' : 's'}`
  })()

  if (!violation) return null

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="sm"
      title={isEditMode ? 'Edit waiver' : 'Add waiver'}
      subtitle="A waiver records why this finding stays open."
      secondaryAction={{ label: 'Cancel', onClick: onClose }}
      primaryAction={{
        label: isEditMode ? 'Update waiver' : 'Add waiver',
        onClick: handleSubmit,
        loading: isSubmitting,
      }}
    >
      <div className="space-y-6">
        {/* The finding this waiver covers */}
        <div className="bg-layer-1 p-4">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <SeverityGlyph level={severityFromApi(violation.criticality)} />
            <span className="font-mono text-helper text-text-helper">{violation.policyCode}</span>
          </div>
          <p className="m-0 mt-2 text-body font-semibold text-text-primary">{violation.policyTitle}</p>
          <p className="m-0 mt-1 text-body text-text-secondary">{violation.message}</p>
        </div>

        <RadioGroup
          name="waiver-type"
          legend="Waiver type"
          variant="tile"
          value={waiverType}
          onChange={(value) => setWaiverType(value as WaiverType)}
          options={waiverTypes.map((option) => ({
            value: option.value,
            label: option.label,
            helper: option.description,
          }))}
        />

        {/* Expiration date for temporary waivers */}
        {waiverType === 'Temporary' && (
          <div>
            <DateInput
              id="expiresAt"
              label="Expiration date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              min={getMinDate()}
              error={errors.expiresAt}
            />
            {expiryPreview && !errors.expiresAt && (
              <p className="m-0 mt-1 text-helper text-text-helper" aria-live="polite">
                {expiryPreview}
              </p>
            )}
          </div>
        )}

        <TextArea
          id="reason"
          label="Reason"
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Explain why this control doesn't apply, or how the risk is covered."
          error={errors.reason}
          helper={`${reason.trim().length} of 20 characters minimum`}
        />

        {/* A critical (SHALL) requirement: say what waiving it costs. */}
        {violation.criticality === 'SHALL' && (
          <InlineNotification kind="info" title="This is a critical control.">
            Waiving it can weaken your security posture and change your compliance status.
          </InlineNotification>
        )}
      </div>
    </Modal>
  )
}
